import React, { useState, useEffect, useMemo } from 'react';
import { Form, Input, Button, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { GET_ALL_DSPS } from '../../../../graphql/queries/getAllDsps';
import InputFilterItem from '../../../delivery/components/InputFilterItem';
import { useFilterContainerClassName, useFilterInputContainerClassName } from '../../../../utils/languageUtils';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';

interface DriverVerificationFiltersProps {
    filters: {
        driverId?: number;
        driverName?: string;
        driverPhone?: string;
        dspId?: number;
    };
    onFilter: (filters: {
        driverId?: number;
        driverName?: string;
        driverPhone?: string;
        dspId?: number;
    }) => void;
    onReset: () => void;
}

interface DspOption {
    label: string;
    value: number;
}

const DriverVerificationFilters: React.FC<DriverVerificationFiltersProps> = ({
    filters,
    onFilter,
    onReset,
}) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    
    // 获取车队列表
    const { data: dspsData, loading: dspLoading } = useQuery(GET_ALL_DSPS);
    const dspOptions: DspOption[] = dspsData?.allDsps?.map((dsp: any) => ({
        label: dsp.name,
        value: Number(dsp.id), // 转换为数字，与 filters.dspId 类型一致
    })) || [];
    
    const handleSubmit = () => {
        form.validateFields().then((values) => {
            onFilter({
                driverId: values.driverId ? Number(values.driverId) : undefined,
                driverName: values.driverName,
                driverPhone: values.driverPhone,
                dspId: values.dspId,
            });
        });
    };
    
    const handleReset = () => {
        form.resetFields();
        onReset();
    };
    
    // 构建初始值，但不包含 dspId（避免在 dspOptions 加载完成前显示数字）
    const initialValues = useMemo(() => ({
        driverId: filters.driverId,
        driverName: filters.driverName,
        driverPhone: filters.driverPhone,
        // dspId 不在这里设置，完全依赖 useEffect 来设置
    }), [filters.driverId, filters.driverName, filters.driverPhone]);
    
    // 同步 filters 到表单字段值
    // 确保在 dspOptions 加载完成后再设置 dspId，避免显示数字而不是车队名称
    useEffect(() => {
        // 如果 dspId 存在，需要确保 dspOptions 已经加载完成且能找到对应的选项
        if (filters.dspId) {
            if (dspLoading) {
                // 还在加载中，等待加载完成
                return;
            }
            // 检查 dspOptions 中是否存在对应的选项
            const dspExists = dspOptions.some(option => option.value === filters.dspId);
            if (!dspExists) {
                // 如果找不到对应的选项，可能是数据还没加载完成，等待一下
                return;
            }
        }
        
        form.setFieldsValue({
            driverId: filters.driverId,
            driverName: filters.driverName,
            driverPhone: filters.driverPhone,
            dspId: filters.dspId,
        });
    }, [filters, form, dspLoading, dspOptions]);

    // 使用统一的样式 Hook
    const containerClassName = useFilterContainerClassName();
    const inputContainerClassName = useFilterInputContainerClassName();

    // 定义列标签数组用于对齐计算
    const column1Labels = [t('riskControl.driverVerification.filters.driverId'), t('riskControl.driverVerification.filters.dsp')];
    const column2Labels = [t('riskControl.driverVerification.filters.driverName')];
    const column3Labels = [t('riskControl.driverVerification.filters.driverPhone')];

    return (
        <div className="mb-2 py-2 pl-0">
            <Form
                form={form}
                initialValues={initialValues}
            >
                {/* 使用 Grid 布局确保列对齐 - 三等分布局 */}
                <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                    {/* 第一列 - 统一标签宽度 */}
                    <div className="min-w-0">
                        <InputFilterItem
                            label={t('riskControl.driverVerification.filters.driverId')}
                            value={filters.driverId?.toString() || ''}
                            onChange={(e) => {
                                const value = e.target.value.trim();
                                form.setFieldsValue({ driverId: value ? Number(value) : undefined });
                                onFilter({
                                    ...filters,
                                    driverId: value ? Number(value) : undefined
                                });
                            }}
                            spanClassName={getSmartLabelClassName(column1Labels)}
                        />
                    </div>
                    {/* 第二列 - 统一标签宽度 */}
                    <div className="min-w-0">
                        <InputFilterItem
                            label={t('riskControl.driverVerification.filters.driverName')}
                            value={filters.driverName || ''}
                            onChange={(e) => {
                                const value = e.target.value.trim();
                                form.setFieldsValue({ driverName: value || undefined });
                                onFilter({
                                    ...filters,
                                    driverName: value || undefined
                                });
                            }}
                            spanClassName={getSmartLabelClassName(column2Labels)}
                        />
                    </div>
                    {/* 第三列 - 统一标签宽度 */}
                    <div className="min-w-0">
                        <InputFilterItem
                            label={t('riskControl.driverVerification.filters.driverPhone')}
                            value={filters.driverPhone || ''}
                            onChange={(e) => {
                                const value = e.target.value.trim();
                                form.setFieldsValue({ driverPhone: value || undefined });
                                onFilter({
                                    ...filters,
                                    driverPhone: value || undefined
                                });
                            }}
                            spanClassName={getSmartLabelClassName(column3Labels)}
                        />
                    </div>
                    {/* 第二行 - DSP选择 */}
                    <div className="min-w-0">
                        <div className={containerClassName}>
                            <span className={getSmartLabelClassName(column1Labels)}>
                                {t('riskControl.driverVerification.filters.dsp')}
                            </span>
                            <div className={inputContainerClassName}>
                                <Select
                                    placeholder={t('riskControl.driverVerification.filters.dsp') as string}
                                    className="w-full"
                                    size="middle"
                                    allowClear
                                    options={dspOptions}
                                    loading={dspLoading}
                                    showSearch
                                    filterOption={(input, option) =>
                                        String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    value={filters.dspId}
                                    onChange={(value) => {
                                        form.setFieldsValue({ dspId: value });
                                        onFilter({
                                            ...filters,
                                            dspId: value
                                        });
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="min-w-0"></div>
                    <div className="min-w-0"></div>
                </div>
            </Form>
            
            {/* 按钮区域 */}
            <div className="flex justify-end gap-2 mb-3 mt-4">
                <Button
                    type="primary"
                    onClick={handleSubmit}
                    className="w-auto min-w-24"
                >
                    <span className="font-bold">{t('components.button.filter')}</span>
                </Button>
                <Button
                    onClick={handleReset}
                    className="w-auto min-w-24"
                >
                    <span className="font-bold">{t('components.button.reset')}</span>
                </Button>
            </div>
        </div>
    );
};

export default DriverVerificationFilters;

